import React, {
  createContext,
  useState,
  memo,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { api, toPrice, isAndroidApp, sendErrorEmail } from "utils";
import { useHeaders } from "hooks";
import { month } from "pages/Challenges/Presentation/components/messages";
import { useModal } from "contexts/Modal/useModal";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import {
  accessPremium,
  createFirstChallenge,
  premiumApproved,
} from "./messages";

import { i18n } from "translate/i18n";
import useRequest from "hooks/useRequest";

const ANDROID_SUBSCRIPTION_ID = "bibleplan_subscription_0";

export const UserContext = createContext();

export const UserProvider = ({ children, saveUserPicture, user }) => {
  const [premium, setPremium] = useState({ notLoaded: true });
  const headers = useHeaders();
  const isLogged = useMemo(() => !!user.token, [user.token]);
  const [isLoadingPremiumInfo, setIsLoadingPremiumInfo] = useState(true);
  const [shouldUpdatePremiumInfo, setShouldUpdatePremiumInfo] = useState(null);
  const { createModal, closeModal } = useModal();
  const history = useHistory();

  const request = useRequest();

  useEffect(() => {
    if (!user.picture) {
      request
        .get("/user/picture")
        .then((response) => saveUserPicture(response.data.picture));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.picture]);

  useEffect(() => {
    if (shouldUpdatePremiumInfo) {
      const newPremium = { ...premium, ...shouldUpdatePremiumInfo };

      setPremium({
        ...newPremium,
        notPremium: false,
        amountPurchases: premium?.amountPurchases
          ? premium.amountPurchases + 1
          : 1,
      });

      setShouldUpdatePremiumInfo(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldUpdatePremiumInfo]);

  useEffect(() => {
    isLogged && getSubscription();
    isLogged && handleUserLanguage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (premium.origin === "android" && premium.purchaseToken) {
      api
        .post(
          "/checkout/android/validate",
          {
            purchaseToken: premium.purchaseToken,
            subscription_id: "bibleplan_subscription_0",
          },
          headers
        )
        .then((response) => {
          const status = response?.data?.status;
          const canceledOrExpired =
            status === "canceled" ||
            status === "expired" ||
            response.data.canceled ||
            response.data.expired;

          if (canceledOrExpired) {
            api.delete("/premium/android", headers);
            return setPremium({ notPremium: true });
          }

          const premiumGranted =
            status === "granted" || response.data.premiumGranted;

          console.log("debug response", {
            pendingApproval: premium.pendingApproval,
            premiumGranted,
            premium,
            response,
          });

          if (premium.pendingApproval && premiumGranted) {
            setPremium({
              ...premium,
              notPremium: false,
              pendingApproval: false,
            });

            api.delete("/checkout/android/pending", headers).then(() => {
              createModal({
                title: premiumApproved,
                subtitle: accessPremium,
                firstButtonText: createFirstChallenge,
                onFirstButtonClick: () => {
                  closeModal();
                  history.push("/challenges/new/name");
                },
              });
            });
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [premium]);

  const handleUserLanguage = async () => {
    const { data } = await api.get("/language", headers);

    const languageIsDifferent = data.language !== i18n.language;

    if (!data.language || languageIsDifferent) {
      await api.post("/language", { language: i18n.language }, headers);
    }
  };

  const getSubscription = async () => {
    setIsLoadingPremiumInfo(true);

    const { data } = await api.get("/premium", headers);

    if (!data.premiumAccess) {
      setPremium({ notPremium: true });
      return setIsLoadingPremiumInfo(false);
    }

    const name = data?.premiumAccess?.product_title;

    const price = toPrice({
      cents: data?.premiumAccess?.amount_paid,
      currency: data?.premiumAccess?.currency_paid,
    });

    let title = `${name} ${price}`;

    const isLegacyAndroidOneTime =
      data?.premiumAccess?.origin === "android" &&
      data?.premiumAccess?.type === "one_time";
    const normalizedPremiumType = isLegacyAndroidOneTime
      ? "subscription"
      : data.premiumAccess.type;

    if (normalizedPremiumType === "subscription") {
      title += `/${month}`;
    }

    setPremium({
      title,
      type: normalizedPremiumType,
      customerId: data.premiumAccess.customer_id,
      subscriptionId: data.premiumAccess?.subscription_id,
      amountPurchases: data.premiumAccess?.amountPurchases,
      purchaseToken: data.premiumAccess?.purchaseToken,
      origin: data.premiumAccess?.origin,
      pendingApproval: data.premiumAccess?.pendingApproval,
      notPremium: data.premiumAccess?.pendingApproval,
    });

    setIsLoadingPremiumInfo(false);
  };

  const updatePremiumInfo = useCallback((premium) => {
    setShouldUpdatePremiumInfo(premium);
  }, []);

  // Android safety net (ABR-259). A purchase whose /validate or /save never completed
  // (app closed, network error, Play API lag right after paying) is never acknowledged,
  // and Google refunds it after 3 days. On startup we list the purchases Play knows
  // about and validate/save the ones the backend still doesn't have.
  useEffect(() => {
    if (!isLogged || !isAndroidApp || premium.notLoaded) return;
    if (!("getDigitalGoodsService" in window)) return;

    const recoveryKey = "android:purchase-recovery";
    try {
      if (window.sessionStorage.getItem(recoveryKey)) return;
      window.sessionStorage.setItem(recoveryKey, `${Date.now()}`);
    } catch (error) {
      // sessionStorage unavailable: run anyway, once per mount.
    }

    const recoverAndroidPurchases = async () => {
      const service = await window.getDigitalGoodsService(
        "https://play.google.com/billing"
      );
      if (!service || typeof service.listPurchases !== "function") return;

      const purchases = (await service.listPurchases()) || [];
      const candidates = purchases.filter(
        (purchase) =>
          purchase?.itemId === ANDROID_SUBSCRIPTION_ID &&
          purchase?.purchaseToken &&
          purchase.purchaseToken !== premium.purchaseToken
      );

      if (!candidates.length) return;

      let details = null;
      try {
        const [item] = await service.getDetails([ANDROID_SUBSCRIPTION_ID]);
        details = item || null;
      } catch (error) {
        details = null;
      }

      for (const purchase of candidates) {
        const { data } = await api.post(
          "/checkout/android/validate",
          {
            purchaseToken: purchase.purchaseToken,
            subscription_id: ANDROID_SUBSCRIPTION_ID,
          },
          headers
        );

        console.log("[android][recovery] validate", {
          status: data?.status,
          acknowledged: data?.acknowledged,
          debugId: data?.debugId,
        });

        if (data?.status !== "granted") continue;

        const amountPaid = Math.round((details?.price?.value || 0) * 100);

        try {
          await api.post(
            "/checkout/android/save",
            {
              type: "subscription",
              product_title: details?.title || "Bible Journey Premium",
              amount_paid: amountPaid,
              currency_paid: details?.price?.currency || "",
              purchaseToken: purchase.purchaseToken,
            },
            headers
          );
        } catch (error) {
          // 409 = token already linked to another account; nothing to recover here.
          if (error?.response?.status === 409) continue;
          throw error;
        }

        updatePremiumInfo({
          type: "subscription",
          title: `${details?.title || "Bible Journey Premium"}/${month}`,
          origin: "android",
          purchaseToken: purchase.purchaseToken,
        });
        return;
      }
    };

    recoverAndroidPurchases().catch((error) => {
      console.error(error);
      sendErrorEmail(error, "on contexts/User: recoverAndroidPurchases");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLogged, premium.notLoaded]);

  return (
    <UserContext.Provider
      value={{
        ...user,
        premium,
        isLogged,
        isLoadingPremiumInfo,
        updatePremiumInfo,
        getSubscription,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export default memo(UserProvider);
